import type { RefundOrderDetailVo } from 'services/api';
import type { MockRefundOrder } from '../mock';

/**
 * 退款单表单扁平字段 — 详情对象（VO/mock）→ FormModel 字段映射的产物
 *
 * FormModel 只读字段（l2/l3/refund_no/channel/预算金额/执行日期）经 formItemKeyMap
 * 绑定到这些扁平字段；optimizers 为详情人员对象数组（提交时调用方转 optimizer_ids）。
 */
export interface RefundDetailData {
  l2_demand_id?: number | null;
  l3_demand_id?: number | null;
  refund_no?: string;
  amount?: number;
  currency?: string;
  account_id_name?: string;
  channel?: string;
  platform?: string;
  /** 投放渠道可选范围（草稿 VO channel_options，可编辑下拉选项） */
  channel_options?: string[];
  /** 合作平台可选范围（草稿 VO platform_options，可编辑下拉选项） */
  platform_options?: string[];
  /** 一级需求类型ID（草稿 VO l1_category_id，前端渠道/平台显隐判断） */
  l1_category_id?: number;
  budget?: number;
  execute_date_start?: string;
  execute_date_end?: string;
  region?: string;
  status?: string;
  /** 二级单状态枚举（DemandState.name()，状态 tag 颜色映射） */
  l2_demand_state?: string;
  /** 二级单状态中文名（如"商务评估"） */
  l2_demand_state_name?: string;
  /** 三级单状态枚举（未生成三级单为 null） */
  l3_demand_state?: string;
  /** 三级单状态中文名（未生成三级单为 null） */
  l3_demand_state_name?: string;
  /** 详情人员对象数组（含 user_id/name），提交时转 optimizer_ids（id 数组） */
  optimizers?: { user_id?: number | string; name?: string; user_name?: string }[];
  refund_remark?: string;
  refund_attachments?: { id: number; name?: string }[];
  demand_category_name?: string;
  initiator_role?: string;
}

/**
 * 详情对象 → 表单扁平字段映射（单条抽屉共用）
 *
 * 合并 CreateRefundModal edit 模式与 RefundDetailDrawer 两份几乎相同的映射：
 * - 兼容真实详情 VO（RefundOrderDetailVo：order_id/attachment_ids）与 mock
 *   （MockRefundOrder：id/refund_attachments）
 * - 退款说明/退款附件分开存储（PRD：长文本+附件，至少一个不为空）
 */
/**
 * 详情附件 → 上传组件 fileList（{id, name}）。
 * 优先后端返回的附件对象 attachments[].blob.filename（含文件名，外包详情修复附件显示为原始 URL 问题）；
 * fallback refund_attachments（旧结构）→ attachment_ids（纯 id，name 留空）。
 */
const toFormAttachments = (detail: any) => {
  if (Array.isArray(detail.attachments) && detail.attachments.length) {
    return detail.attachments.map((a: any) => ({
      id: a.id,
      name: a.blob?.filename ?? '',
    }));
  }
  return (
    detail.refund_attachments ??
    (detail.attachment_ids ?? []).map((id: number) => ({ id, name: '' }))
  );
};

export const buildDetailFromRecord = (
  record: RefundOrderDetailVo | MockRefundOrder | Record<string, any> | null
): RefundDetailData => {
  const detail: any = record ?? {};
  return {
    l2_demand_id: detail.l2_demand_id ?? null,
    l3_demand_id: detail.l3_demand_id ?? null,
    refund_no: detail.refund_no ?? '',
    amount: detail.amount,
    currency: detail.currency ?? 'RMB',
    account_id_name: detail.account_id_name ?? '',
    channel: detail.channel ?? '',
    platform: detail.platform ?? '',
    channel_options: detail.channel_options,
    platform_options: detail.platform_options,
    l1_category_id: detail.l1_category_id,
    budget: detail.budget,
    execute_date_start: detail.execute_date_start,
    execute_date_end: detail.execute_date_end,
    region: detail.region ?? 'DOMESTIC',
    status: detail.status ?? 'PENDING_PLANNER_INITIATE',
    optimizers: toFormOptimizers(detail.optimizers),
    demand_category_name: detail.demand_category_name,
    initiator_role: detail.initiator_role,
    l2_demand_state: detail.l2_demand_state,
    l2_demand_state_name: detail.l2_demand_state_name,
    l3_demand_state: detail.l3_demand_state,
    l3_demand_state_name: detail.l3_demand_state_name,
    refund_remark: detail.refund_remark ?? '',
    refund_attachments: toFormAttachments(detail),
  };
};

/**
 * create 模式：基于草稿接口预填 + 默认值（替代 mock 占位，前端不再硬编码）
 *
 * 草稿接口返回只读字段（budget/执行日期/渠道/币种/l2/l3/optimizers），
 * 无草稿数据则空表单（amount 留空、refund_no 自动生成、status 待策划发起）。
 */
export const buildDetailFromDraft = (
  draftData: RefundDetailData | null,
  l2DemandId?: number,
  l3DemandId?: number
): RefundDetailData => ({
  l2_demand_id: draftData?.l2_demand_id ?? l2DemandId,
  l3_demand_id: draftData?.l3_demand_id ?? l3DemandId ?? null,
  refund_no: '',
  amount: undefined,
  currency: draftData?.currency ?? 'RMB',
  account_id_name: draftData?.account_id_name ?? '',
  channel: draftData?.channel ?? '',
  platform: draftData?.platform ?? '',
  channel_options: draftData?.channel_options,
  platform_options: draftData?.platform_options,
  l1_category_id: draftData?.l1_category_id,
  budget: draftData?.budget,
  execute_date_start: draftData?.execute_date_start,
  execute_date_end: draftData?.execute_date_end,
  region: draftData?.region ?? 'DOMESTIC',
  status: 'PENDING_PLANNER_INITIATE',
  initiator_role: draftData?.initiator_role,
  l2_demand_state: draftData?.l2_demand_state,
  l2_demand_state_name: draftData?.l2_demand_state_name,
  l3_demand_state: draftData?.l3_demand_state,
  l3_demand_state_name: draftData?.l3_demand_state_name,
  optimizers: toFormOptimizers(draftData?.optimizers),
  refund_remark: '',
  refund_attachments: [],
});

/**
 * 后端人员 VO（UserSimpleVo：{id, name}）→ 表单 select 消费结构 {user_id, name, user_name}。
 *
 * 背景：详情/草稿接口返回 optimizers 为 UserSimpleVo[]（用户ID字段是 id），而表单 select 用
 * userSelectFormat.valueToSelectKey 取 value?.user_id 映射已选值。若不转换，原本已填写的优化师
 * 会因 user_id 缺失而显示为空（"优化师未填写"却实际有值）。同时兼容 mock RefundOptimizer（user_id）。
 */
type FormOptimizer = { user_id?: number | string; name?: string; user_name?: string };
const toFormOptimizers = (
  list?: (FormOptimizer & { id?: number })[]
): FormOptimizer[] =>
  (list ?? [])
    // 过滤无 user_id/id 的空对象：外包端偶发返回脏数据（无 id 元素）会让 select view
    // valueToSelectKey 产生空 key，回显 join 出孤立顿号/逗号（如「，」）
    .filter((o) => o?.user_id != null || o?.id != null)
    .map((o) => ({
      user_id: o.user_id ?? o.id,
      name: o.name ?? '',
      user_name: o.user_name ?? o.name ?? '',
    }));
